/**
 * Remember which file holds a card's artwork, between mounts and between
 * launches.
 *
 * The ranked ten holds card ids, not pictures. `catalog_browse` carries no image
 * column and `wish_list.image_url` is null on every row written before the add
 * path started filling it, so the grid cannot paint a single tile until a
 * separate `card_variants` lookup answers. That lookup is fast on the server and
 * still cost about 800 ms of black squares on wifi, every single time the chase
 * tab mounted, for ten URLs that had not changed since the last time it was
 * open.
 *
 * Ids are stable. The file behind one is content-hashed and does change when the
 * art is re-mirrored, which is the reason nothing in this app hard-codes an art
 * URL - but a stale URL here is not a broken tile: CardImage falls through the
 * original and the second catalogue, and the document-level relay in artRelay
 * re-fetches and re-stores anything that 404s. So this is safe to remember, and
 * the cost of it being wrong is one retry rather than a blank card.
 *
 * Entries expire so a re-mirror is picked up within a week without anybody
 * having to clear anything.
 */

const KEY = 'cc.cardArt.v1';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
// Only the handful-of-cards lookups come through here - the ranked ten and the
// five-card fan - so the ceiling is about keeping a corrupt or runaway write
// from filling the quota, not about capacity.
const MAX_ENTRIES = 500;

// Read once per session. localStorage is synchronous and this is consulted on
// the first render of the chase tab, which is the one place in the app where a
// blocking read would show.
let memory = null;

function load() {
  if (memory) return memory;
  memory = new Map();
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(KEY) : null;
    if (raw) {
      const parsed = JSON.parse(raw);
      for (const [id, entry] of Object.entries(parsed || {})) {
        if (Array.isArray(entry) && typeof entry[0] === 'string') memory.set(id, entry);
      }
    }
  } catch {
    // A corrupt or unavailable store is a cold cache, not an error worth
    // surfacing. The lookup it was standing in for still runs.
  }
  return memory;
}

function persist() {
  try {
    if (typeof localStorage === 'undefined') return;
    const entries = [...memory.entries()];
    // Oldest out first when over the ceiling.
    const kept = entries.length > MAX_ENTRIES
      ? entries.sort((a, b) => b[1][1] - a[1][1]).slice(0, MAX_ENTRIES)
      : entries;
    localStorage.setItem(KEY, JSON.stringify(Object.fromEntries(kept)));
    memory = new Map(kept);
  } catch {
    // Quota or private mode. The in-process map still does its job for the rest
    // of the session.
  }
}

/**
 * What is already known about these cards, and what still has to be looked up.
 *
 * @param {string[]} cardIds
 * @returns {{ found: Record<string, string>, missing: string[] }}
 */
export function recallCardArt(cardIds) {
  const store = load();
  const found = {};
  const missing = [];
  const now = Date.now();
  for (const id of cardIds || []) {
    if (!id) continue;
    const entry = store.get(id);
    if (entry && now - entry[1] < MAX_AGE_MS) found[id] = entry[0];
    else missing.push(id);
  }
  return { found, missing };
}

/**
 * @param {Record<string, string>} map card id to artwork URL
 */
export function rememberCardArt(map) {
  const store = load();
  const now = Date.now();
  let changed = false;
  for (const [id, url] of Object.entries(map || {})) {
    if (!id || typeof url !== 'string' || !url) continue;
    store.set(id, [url, now]);
    changed = true;
  }
  if (changed) persist();
}

/** Test seam. Drops both the in-process map and the stored copy. */
export function forgetCardArt() {
  memory = new Map();
  try {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(KEY);
  } catch {
    // Nothing to drop.
  }
}

export default { recallCardArt, rememberCardArt, forgetCardArt };
